import { backendApi } from "@/services/api";
import { Filter } from "@/shared/lib/filter/filter";
import { Differentiation, DifferentiationResult } from "./differentiation";

const differentiationApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    createDifferentiation: build.mutation<Response, CreateRequest>({
      query: (request) => ({
        url: `/inspections/differentiation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["Differentiation", "Countings"],
    }),
    putDifferentiation: build.mutation<
      Differentiation,
      PutDifferentiationRequest
    >({
      query: (request) => ({
        url: `/inspections/differentiation`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["Differentiation"],
    }),
    deleteDifferentiation: build.mutation<
      DeleteDifferentiationResponse,
      DeleteDifferentiationRequest
    >({
      query: (request) => ({
        url: `/inspections/differentiation`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["Differentiation"],
    }),
    listDifferentiations: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/inspections/differentiation`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: ["Differentiation"],
    }),
    putResult: build.mutation<DifferentiationResult, PutResultRequest>({
      query: (request) => ({
        url: `/inspections/differentiation/result`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["DifferentiationResults"],
    }),
    putResults: build.mutation<PutResultsResponse, PutResultsRequest>({
      query: (request) => ({
        url: `/inspections/differentiation/results`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["DifferentiationResults"],
    }),
    deleteResult: build.mutation<DeleteResultResponse, DeleteResultRequest>({
      query: (request) => ({
        url: `/inspections/differentiation/result`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["DifferentiationResults"],
    }),
    listResults: build.query<ResultList, ListResultsRequest>({
      query: (request) => ({
        url: `/inspections/differentiation/results`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: ["DifferentiationResults"],
    }),
    showDifferentiation: build.query<Response, { id: string }>({
      query: (request) => ({
        url: `/inspections/differentiation/${request.id}`,
        method: "GET",
      }),
      providesTags: (_result, _e, request) => [
        { type: "Differentiation", id: request.id },
      ],
    }),
    putSampleSetFlags: build.mutation<
      SampleSetFlagsResponse,
      PutSampleSetFlagsRequest
    >({
      query: (request) => ({
        url: `/inspections/differentiation/flags`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["DifferentiationResults"],
    }),
    showSampleSetFlags: build.query<
      SampleSetFlagsResponse,
      ShowSampleSetFlagsRequest
    >({
      query: (request) => ({
        url: `/inspections/differentiation/flags`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: ["DifferentiationResults"],
    }),
  }),
});

export const {
  useCreateDifferentiationMutation,
  usePutDifferentiationMutation,
  useDeleteDifferentiationMutation,
  useListDifferentiationsQuery,
  useListResultsQuery,
  usePutResultMutation,
  usePutResultsMutation,
  useDeleteResultMutation,
  useShowDifferentiationQuery,
  usePutSampleSetFlagsMutation,
  useShowSampleSetFlagsQuery,
} = differentiationApi;

export interface Response {
  differentiation: Differentiation;
}

export interface CreateRequest {
  id: string;
  processId: string;
  customerId: string;
  countingId: string;
  createdBy: string;
}

export interface PutDifferentiationRequest {
  mouldId: string;
  sampleSetId: string;
  processId: string;
  value?: number;
  overgrown?: boolean;
}

export interface ListRequest {
  processId?: Filter<string>;
  ownerId?: Filter<string>;
  ownerType?: Filter<string>;
  limit: number;
  offset: number;
}

export interface List {
  count: number;
  items: Differentiation[];
}

export interface DeleteDifferentiationRequest {
  mouldId: string;
  sampleSetId: string;
}

export interface DeleteDifferentiationResponse {}

export interface PutResultRequest {
  result: DifferentiationResult;
}

export interface PutResultsRequest {
  differentiationId: string;
  sampleSetId: string;
  results: DifferentiationResult[];
}

export interface PutResultsResponse {}

export interface DeleteResultRequest {
  id: string;
}

export interface DeleteResultResponse {}

export interface PutSampleSetFlagsRequest {
  differentiationId: string;
  sampleSetId: string;
  mouldExceeds: boolean;
  yeastExceeds: boolean;
}

export interface ShowSampleSetFlagsRequest {
  differentiationId: string;
  sampleSetId: string;
}

export interface SampleSetFlagsResponse {
  mouldExceeds: boolean;
  yeastExceeds: boolean;
}

export interface ListResultsRequest {
  sampleSetId?: Filter<string>;
  differentiationId?: Filter<string>;
  mouldId?: Filter<string>;
  limit: number;
  offset: number;
}

export interface ResultList {
  count: number;
  items: DifferentiationResult[];
}
